import React, { useEffect, useRef, useState } from 'react';
import type { ToolUseBlock } from '../../store/chatStore';
import { useChatStore } from '../../store/chatStore';
import { useToolResultContent } from './ToolResultDataContext';
import {
  langFromPath,
  langColor,
  Spinner,
  TrafficLights,
  CollapseButton,
} from './codeBlockUtils';

import { useTranslation } from 'react-i18next';

function basename(p: string) {
  return p.split('/').pop() ?? p;
}
function dirname(p: string) {
  return p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '';
}

/** A single line in the unified diff */
const DiffLine: React.FC<{ type: 'removed' | 'added'; text: string }> = ({
  type,
  text,
}) => {
  const isRemoved = type === 'removed';
  return (
    <div
      className={`flex items-stretch ${isRemoved ? 'bg-red-950/25 hover:bg-red-950/35' : 'bg-emerald-950/20 hover:bg-emerald-950/30'} transition-colors duration-75`}
    >
      <span
        className={`flex w-7 flex-shrink-0 items-center justify-center text-xs font-semibold select-none ${
          isRemoved
            ? 'bg-red-950/50 text-red-400/70'
            : 'bg-emerald-950/40 text-emerald-400/70'
        }`}
        style={{
          borderRight: `1px solid ${isRemoved ? 'rgba(127,29,29,0.3)' : 'rgba(6,78,59,0.3)'}`,
        }}
      >
        {isRemoved ? '−' : '+'}
      </span>
      <span
        className={`flex-1 px-3 font-mono text-xs select-text ${
          isRemoved ? 'text-red-200/75' : 'text-emerald-200/80'
        }`}
        style={{ lineHeight: '1.65', whiteSpace: 'pre' }}
      >
        {text || ' '}
      </span>
    </div>
  );
};

/**
 * Renders workspaceEditFile as a unified diff (removed lines then added lines).
 */
export const EditFileBlock: React.FC<{ content: ToolUseBlock }> = ({
  content,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [collapsed, setCollapsed] = useState(true);
  const userInteracted = useRef(false);

  const handleCollapseToggle = () => {
    userInteracted.current = true;
    setCollapsed((v) => !v);
  };

  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const isPending = useChatStore((s) =>
    activeConversationId
      ? (
          s.conversations[activeConversationId]?.pendingToolUses ?? new Set()
        ).has(content.id)
      : false,
  );
  const resultJson = useToolResultContent(content.id);

  // `path` is the new param name; `filePath` is the legacy name kept for old conversations
  const filePath =
    typeof content.input?.path === 'string' ? content.input.path :
    typeof content.input?.filePath === 'string' ? content.input.filePath : '';
  const oldString =
    typeof content.input?.oldString === 'string' ? content.input.oldString : '';
  const newString =
    typeof content.input?.newString === 'string' ? content.input.newString : '';
  const session =
    typeof content.input?.session === 'string' ? content.input.session : 'main';
  const replaceAll = content.input?.replaceAll === true;

  const lang = langFromPath(filePath);
  const color = langColor(lang);
  const name = basename(filePath);
  const dir = dirname(filePath);
  const isRunning = isPending || !resultJson;

  // Open while running, close when done — unless user has manually toggled
  useEffect(() => {
    if (userInteracted.current) {
      return;
    }
    if (isRunning) {
      setCollapsed(false);
    } else {
      const t = setTimeout(() => setCollapsed(true), 700);
      return () => clearTimeout(t);
    }
  }, [isRunning]);

  let replacements: number | null = null;
  let isError = false;
  let errorMessage = '';

  if (resultJson) {
    try {
      const parsed = JSON.parse(resultJson);
      if (typeof parsed === 'object' && parsed?.replacements != null) {
        replacements = parsed.replacements as number;
      } else if (typeof parsed === 'string') {
        const lower = parsed.toLowerCase();
        if (lower.includes('error') || lower.includes('not found')) {
          isError = true;
          errorMessage = parsed;
        } else {
          const m = parsed.match(/replaced (\d+)/);
          replacements = m ? parseInt(m[1], 10) : 1;
        }
      }
    } catch {
      isError = true;
      errorMessage = resultJson;
    }
  }

  const oldLines = oldString.trimEnd().split('\n');
  const newLines = newString === '' ? [] : newString.trimEnd().split('\n');

  return (
    <div className="my-1 overflow-hidden rounded-xl border border-gray-800 bg-gray-950 text-xs shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-gray-800 bg-gray-900/80 px-3 py-1.5">
        <TrafficLights />
        <span
          className="h-2 w-2 flex-shrink-0 rounded-full"
          style={{ background: color }}
        />
        <svg
          className="h-3.5 w-3.5 flex-shrink-0 text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.75"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
          />
        </svg>

        <span className="flex-1 truncate font-mono text-xs">
          {dir && <span className="text-gray-500">{dir}/</span>}
          <span className="text-gray-200">{name || filePath}</span>
        </span>

        <div className="flex items-center gap-1.5">
          {replacements != null && (
            <span className="rounded-md bg-emerald-950/60 px-1.5 py-0.5 text-xs font-medium text-emerald-400">
              {t('edits', { count: replacements })}
            </span>
          )}
          {replaceAll && (
            <span className="rounded-md bg-gray-800/80 px-1.5 py-0.5 text-xs text-gray-500">
              {t('all')}
            </span>
          )}
          {session !== 'main' && (
            <span className="rounded-md bg-gray-800/80 px-1.5 py-0.5 text-xs text-gray-500">
              {session}
            </span>
          )}
          <span className="rounded-md bg-gray-800/80 px-1.5 py-0.5 text-xs font-medium tracking-wider text-gray-600 uppercase">
            {lang}
          </span>
        </div>

        <div className="transition-opacity duration-300">
          {isRunning ? (
            <Spinner />
          ) : isError ? (
            <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
          ) : (
            <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
          )}
        </div>

        <CollapseButton collapsed={collapsed} onClick={handleCollapseToggle} />
      </div>
      {!collapsed && (
        <>
          {isRunning ? (
            /* Running — preview removed lines with a subtle shimmer */
            <div>
              <div className="flex items-center gap-1.5 border-b border-gray-800/40 bg-red-950/10 px-3 py-1">
                <Spinner className="text-gray-600" />
                <span className="text-xs text-gray-600">
                  {t('editing')}
                </span>
              </div>
              <div className="overflow-auto" style={{ maxHeight: '14rem' }}>
                {oldLines.map((line, i) => (
                  <DiffLine key={i} type="removed" text={line} />
                ))}
              </div>
            </div>
          ) : isError ? (
            <div className="px-4 py-3 font-mono text-xs text-red-400">
              {errorMessage}
            </div>
          ) : (
            <>
              {/* Unified diff — removed lines then added lines */}
              <div className="overflow-auto" style={{ maxHeight: '20rem' }}>
                {oldLines.map((line, i) => (
                  <DiffLine key={`r-${i}`} type="removed" text={line} />
                ))}
                {/* Separator between removed and added */}
                {newLines.length > 0 && (
                  <div className="border-y border-gray-800/40 bg-gray-900/30 px-3 py-0.5">
                    <span className="text-xs tracking-widest text-gray-700 uppercase">
                      {t('replacement')}
                    </span>
                  </div>
                )}
                {newLines.map((line, i) => (
                  <DiffLine key={`a-${i}`} type="added" text={line} />
                ))}
              </div>

              {/* Footer */}
              <div className="flex items-center gap-3 border-t border-gray-800/60 bg-gray-900/40 px-3 py-1">
                <span className="text-xs text-red-500/60">
                  {t('lRemoved', { length: oldLines.length })}
                </span>
                <span className="text-xs text-gray-700">·</span>
                <span className="text-xs text-emerald-500/60">
                  {t('lAdded', { length: newLines.length })}
                </span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
